/**
 * Pickups + goal (Coin, Mushroom, Flag) — split out of the old
 * PlatformerEntities.js monolith (which barrel-re-exports these).
 */

import { Container, Graphics } from '../../../vendor/pixi.min.mjs';
import { PLATFORMER_PHYSICS, PLATFORMER_ENTITY_DEFS } from './platformerConfig.js';
import { spawnSprite } from './entityHelpers.js';
import { WalkerEnemyBase } from './EnemyEntities.js';

// ── Coin (free-standing) ──────────────────────────────────────────

export class CoinEntity {
    constructor(c, r, board, theme) {
        this._theme = theme;
        const cs = board.cellSize;
        this.cell = { c, r };
        const w = cs * 0.55;
        const h = cs * 0.55;
        this.x = c * cs + (cs - w) / 2;
        this.y = r * cs + (cs - h) / 2;
        this.w = w;
        this.h = h;
        this.alive = true;

        this.display = new Container();
        const def = PLATFORMER_ENTITY_DEFS.coin;
        const sprite = spawnSprite(theme, def.slot, w, h);
        if (sprite) {
            this.display.addChild(sprite);
            this._sprite = sprite;
        } else {
            const g = new Graphics();
            g.circle(w / 2, h / 2, Math.min(w, h) * 0.42);
            g.fill(def.fallbackColor);
            g.circle(w / 2, h / 2, Math.min(w, h) * 0.42);
            g.stroke({ color: 0x78350f, width: 2 });
            this.display.addChild(g);
            this._sprite = g;
        }
        this._bobBase = this.y;
        this._bobTime = 0;
        this.display.x = this.x;
        this.display.y = this.y;
    }

    update() {
        if (!this.alive) return;
        // Idle bob — pure cosmetic.
        this._bobTime += 0.12;
        const offset = Math.sin(this._bobTime) * 2;
        this.display.y = this._bobBase + offset;
    }

    onCollidePlayer() {
        if (!this.alive) return null;
        this.alive = false;
        this.display.visible = false;
        return 'pickup';
    }

    destroy() {
        this.display.removeChildren();
        if (this.display.parent) this.display.parent.removeChild(this.display);
    }
}

// ── Mushroom ──────────────────────────────────────────────────────
//
// Walks horizontally like an enemy but harms nobody: touching it
// powers the player up. Starts inert when spawned from a ?-block
// (rises out of the block first) and joins the regular walker AI
// once it's clear of the spawning tile.

export class MushroomEntity extends WalkerEnemyBase {
    constructor(c, r, board, theme, props = {}) {
        super(c, r, board, theme, { dir: props.dir || 'right' }, 'mushroom');
        this.kind = props.kind || 'super';
        // The tub art carries the brand label — never mirror it.
        this.faceFlip = false;
        // When spawned from a ?-block we're given an "emerge"
        // animation: pin gravity off, slide upward 1 tile, then
        // resume normal AI.
        if (props.emerge) {
            this._noGravity = true;
            this._emergeTarget = this.y - board.cellSize;
            this.vx = 0;
        }
    }

    update(_controls, board) {
        if (!this.alive) return;
        if (this._noGravity) {
            // Slide up at a steady 1 px/frame; once we've reached
            // the target we re-enable gravity + walking AI.
            this.y -= 1.5;
            this.display.x = this.x;
            this.display.y = this.y;
            if (this.y <= this._emergeTarget) {
                this._noGravity = false;
                this.vx = this.dir * PLATFORMER_PHYSICS.enemySpeed;
            }
            return;
        }
        super.update(_controls, board);
    }

    onCollidePlayer() {
        if (!this.alive) return null;
        this.alive = false;
        this.display.visible = false;
        return 'powerup';
    }
}

// ── Checkpoint ────────────────────────────────────────────────────
//
// A friendly mid-level respawn marker. First touch returns the
// 'checkpoint' verdict — the scene stores this cell as the respawn
// point — then the entity stays visible but inert (alive so it keeps
// rendering, `_armed` false so it never re-triggers).

export class CheckpointEntity {
    constructor(c, r, board, theme) {
        const cs = board.cellSize;
        this.cell = { c, r };
        const w = cs * 1.15;
        const h = cs * 1.35;
        this.x = c * cs + (cs - w) / 2;
        this.y = (r + 1) * cs - h;      // feet on the cell floor
        this.w = w;
        this.h = h;
        this.alive = true;
        this._armed = true;

        this.display = new Container();
        const def = PLATFORMER_ENTITY_DEFS.checkpoint;
        const sprite = spawnSprite(theme, def.slot, w, h);
        if (sprite) {
            this.display.addChild(sprite);
            this._sprite = sprite;
        } else {
            const g = new Graphics();
            g.roundRect(w * 0.2, 0, w * 0.6, h, 6);
            g.fill(def.fallbackColor);
            this.display.addChild(g);
            this._sprite = g;
        }
        this._bobTime = Math.random() * Math.PI * 2;
        this.display.x = this.x;
        this.display.y = this.y;
    }

    update() {
        // Idle sway; once activated, a happier faster wiggle.
        this._bobTime += this._armed ? 0.05 : 0.12;
        this.display.rotation = Math.sin(this._bobTime) * (this._armed ? 0.02 : 0.05);
    }

    onCollidePlayer() {
        if (!this.alive || !this._armed) return null;
        this._armed = false;
        return 'checkpoint';
    }

    destroy() {
        this.display.removeChildren();
        if (this.display.parent) this.display.parent.removeChild(this.display);
    }
}

// ── Flag goal ─────────────────────────────────────────────────────

export class FlagGoalEntity {
    constructor(c, r, board, theme) {
        const cs = board.cellSize;
        this.cell = { c, r };
        // Tall hit box that spans the whole pole height — the player
        // wins by touching ANY part of the flag column, not just the
        // base cell.
        this.x = c * cs + cs * 0.45;
        this.y = (r - 7) * cs;
        this.w = cs * 0.10;
        this.h = cs * 8;
        this.alive = true;

        this.display = new Container();
        const def = PLATFORMER_ENTITY_DEFS['flag.goal'];
        // The hit box is a deliberately skinny full-height column —
        // useless as an art box (contain-fitting any PNG into ~6px
        // renders it invisible). Draw the art "cover" against the
        // full pole height instead and pin it to the pole's centre
        // line; flag art ships with generous transparent margins so
        // the overflow never reads as misaligned.
        const sprite = theme?.createSprite?.(def.slot, cs, this.h, { fitMode: 'cover' });
        if (sprite) {
            sprite.x = cs * 0.05;   // hitbox starts 0.45·cs into the cell
            sprite.y = this.h / 2;
            this.display.addChild(sprite);
        } else {
            const g = new Graphics();
            // Pole
            g.rect(0, 0, this.w, this.h);
            g.fill(0xfacc15);
            g.rect(0, 0, this.w, this.h);
            g.stroke({ color: 0x854d0e, width: 1 });
            // Flag triangle
            g.moveTo(this.w, 8);
            g.lineTo(this.w + cs * 0.6, 22);
            g.lineTo(this.w, 36);
            g.closePath();
            g.fill(0xdc2626);
            this.display.addChild(g);
        }
        this.display.x = this.x;
        this.display.y = this.y;
    }

    update() { /* purely visual, no movement */ }

    onCollidePlayer() {
        if (!this.alive) return null;
        this.alive = false;
        return 'win';
    }

    destroy() {
        this.display.removeChildren();
        if (this.display.parent) this.display.parent.removeChild(this.display);
    }
}
